import React, { useImperativeHandle, useState, useCallback, useEffect, useRef } from 'react'
import TokenexInput from 'modules/tokenex/tokenex-input'
import ExpirationDateInput from './expiration-date-input'
import Column from 'modules/ui/atoms/Layout/Column'
import Row from 'modules/ui/atoms/Layout/Row'
import { TokenexCvcInput } from 'modules/tokenex/tokenex-cvc-input'
import { TokenexProvider } from 'modules/tokenex/tokenex-context'
import {
  TokenexCreditCardInputsProps,
  TokenexCreditCardInputsRef,
} from 'modules/tokenex/tokenex-types'
import { useTokenex } from 'modules/tokenex/use-tokenex'
import useOrder from 'modules/order/state/order/use-order'
import Placeloader from 'modules/ui/atoms/Loaders/Placeloader'
import TextPlaceloader from 'modules/ui/atoms/Loaders/TextPlaceloader'
import { validateExpirationDate } from './utils'

export const TokenexCardInputs = React.forwardRef<
  TokenexCreditCardInputsRef,
  TokenexCreditCardInputsProps
>(({ onCardBrand, onLoadingChange }, ref) => {
  // Local state for expiration date
  const [expiryValue, setExpiryValue] = useState('')
  const [expiryError, setExpiryError] = useState<string | null>(null)
  const [hasBlurred, setHasBlurred] = useState(false)

  // Handle expiration date change
  const handleExpiryChange = useCallback(
    (value: string) => {
      setExpiryValue(value)

      // Only show validation errors after the user has interacted with the field
      if (hasBlurred) {
        const error = validateExpirationDate(value)
        setExpiryError(error)
      }
    },
    [hasBlurred],
  )

  // Handle expiration date blur
  const handleExpiryBlur = useCallback(() => {
    setHasBlurred(true)
    const error = validateExpirationDate(expiryValue)
    setExpiryError(error)
  }, [expiryValue])

  // Validate expiration date programmatically
  const validateExpiry = useCallback((): boolean => {
    const error = validateExpirationDate(expiryValue)
    setExpiryError(error)
    return error === null
  }, [expiryValue])

  const tokenexConfig = {
    id: 'cardNumber',
    type: 'CC' as const,
    label: 'Card Number',
    cvvContainerID: 'cvc',
  }

  const order = useOrder()
  const tokenex = useTokenex(tokenexConfig)
  const { tokenexIframe, token, tokenexError, cvvError, isLoading, cardBrand } = tokenex

  const tokenRef = useRef(token)

  useEffect(() => {
    tokenRef.current = token
  }, [token])

  useEffect(() => {
    if (cardBrand) {
      onCardBrand?.(cardBrand)
    }
  }, [cardBrand, onCardBrand])

  useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  // Wait for tokenization to complete with timeout
  const waitForToken = useCallback((maxWaitTime = 5000): Promise<string> => {
    return new Promise((resolve, reject) => {
      const startTime = Date.now()

      const checkToken = () => {
        if (tokenRef.current) {
          resolve(tokenRef.current)
        } else if (Date.now() - startTime > maxWaitTime) {
          reject(new Error('Tokenization timeout - please try again'))
        } else {
          setTimeout(checkToken, 100) // Check every 100ms
        }
      }

      checkToken()
    })
  }, [])

  useImperativeHandle(
    ref,
    () => ({
      savePaymentMethodToStore: async () => {
        if (!tokenexIframe) {
          throw new Error('The tokenex iframe is not defined.')
        }

        // Validate expiration date first
        const isExpiryValid = validateExpiry()

        tokenexIframe?.validate()

        if (tokenexError || !isExpiryValid || cvvError) {
          return { message: tokenexError || expiryError || cvvError }
        }

        // Start tokenization process
        tokenexIframe?.tokenize()

        try {
          // Wait for the token to be available
          const paymentToken = await waitForToken()

          const [month, year] = expiryValue.split(' / ')

          const fourDigitYear = Number(`${new Date().getFullYear().toString().slice(0, 2)}${year}`)

          order.paymentDetails.setMethod({
            paymentToken,
            expirationMonth: Number(month),
            expirationYear: fourDigitYear,
          })

          return Promise.resolve()
        } catch (error) {
          return { message: error instanceof Error ? error.message : 'Tokenization failed' }
        }
      },
      focusCard: () => tokenexIframe?.focus(),
    }),
    [
      tokenexIframe,
      validateExpiry,
      tokenexError,
      cvvError,
      expiryError,
      waitForToken,
      expiryValue,
      order.paymentDetails,
    ],
  )

  return (
    <TokenexProvider tokenex={tokenex}>
      {isLoading && (
        <Column style={{ minHeight: 178 }}>
          <Row style={{ marginBottom: 12 }}>
            <Column>
              <TextPlaceloader w={85} fs="14px" lh="20px" />
              <Placeloader style={{ width: '100%', height: 45, borderRadius: 8 }} />
            </Column>
          </Row>

          <Row style={{ marginBottom: 12 }}>
            <Column style={{ width: '100%' }}>
              <Row>
                <Column style={{ flexGrow: 0.5 }}>
                  <TextPlaceloader w={68} fs="14px" lh="20px" />
                  <Placeloader style={{ width: '100%', height: 45, borderRadius: 8 }} />
                </Column>

                <Column style={{ paddingLeft: '0.5rem', flexGrow: 0.5 }}>
                  <TextPlaceloader w={32} fs="14px" lh="20px" />
                  <Placeloader style={{ width: '100%', height: 45, borderRadius: 8 }} />
                </Column>
              </Row>
            </Column>
          </Row>
        </Column>
      )}

      <Column id="tokenex-card-inputs-container" style={{ display: isLoading ? 'none' : 'block' }}>
        <TokenexInput id="cardNumber" />
        <Row>
          <Column style={{ flex: 1 / 2 }}>
            <ExpirationDateInput
              id="cardExp"
              value={expiryValue}
              onChange={handleExpiryChange}
              onBlur={handleExpiryBlur}
              error={expiryError || undefined}
              required
              label="Expiration Date"
            />
          </Column>
          <Column style={{ flex: 1 / 2, gap: 0 }}>
            <TokenexCvcInput id="cvc" />
          </Column>
        </Row>
      </Column>
    </TokenexProvider>
  )
})

TokenexCardInputs.displayName = 'TokenexCardInputs'
